//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { Alert, Snackbar, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { WarningAmber, InfoOutlined } from '@mui/icons-material';

export type SnackbarSeverity = 'success' | 'error' | 'info' | 'warning';

interface CustomSnackbarProps {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  onClose: () => void;
  autoHideDuration?: number;
  // Optional cap on the toast's width, e.g. for callers whose messages can
  // be long/backend-formatted (400+ chars) and would otherwise render as a
  // full-width banner instead of the platform's usual compact toast.
  // Omitted by default so existing callers keep their exact prior sizing.
  maxWidth?: number | string;
}

/**
 * A custom Snackbar component that can be reused throughout the application
 * with consistent styling and behavior.
 */
const CustomSnackbar: React.FC<CustomSnackbarProps> = ({
  open,
  message,
  severity = 'success',
  onClose,
  autoHideDuration = 6000,
  maxWidth,
}) => {
  const getAlertIcon = (severity: SnackbarSeverity) => {
    switch (severity) {
      case 'error':
        return <WarningAmber sx={{ color: 'rgba(245, 39, 12, 0.91)' }} />;
      case 'info':
        return <InfoOutlined sx={{ color: '#0288d1' }} />;
      case 'success':
      default:
        return <CheckCircleOutlineIcon sx={{ color: '#2e7d32' }} />;
    }
  };

  const getBorderColor = (severity: SnackbarSeverity) => {
    switch (severity) {
      case 'error':
        return '4px solid rgba(245, 39, 12, 0.91)';
      case 'info':
        return '4px solid #0288d1';
      case 'success':
      default:
        return '4px solid #2e7d32';
    }
  };

  return (
    <>
      <div
        id="id-snackbar-live-region"
        data-testid="snackbar-live-region"
        aria-live="polite"
        style={{
          position: 'absolute',
          left: '-9999px',
          height: '1px',
          width: '1px',
          overflow: 'hidden',
        }}
      >
        {open ? message : ''}
      </div>
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={maxWidth !== undefined ? { maxWidth } : undefined}
      >
        <Alert
          onClose={onClose}
          severity={severity}
          variant="outlined"
          icon={getAlertIcon(severity)}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            wordBreak: 'break-word',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem',
            },
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
            },
            bgcolor: 'white',
            color: 'text.primary',
            boxShadow: 1,
            border: '1px solid #e0e0e0',
            borderLeft: getBorderColor(severity),
          }}
        >
          <Typography variant="body2">{message}</Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default CustomSnackbar;

// Helper function to create snackbar state
export const useSnackbar = () => {
  const [snackbarState, setSnackbarState] = React.useState({
    open: false,
    message: '',
    severity: 'success' as SnackbarSeverity,
  });

  const showSnackbar = React.useCallback(
    (message: string, severity: SnackbarSeverity = 'success') => {
      setSnackbarState({
        open: true,
        message,
        severity,
      });
    },
    [],
  );

  const closeSnackbar = React.useCallback(() => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    snackbarState,
    showSnackbar,
    closeSnackbar,
  };
};
